import { rem } from 'polished';
import React from 'react';
import styled from 'styled-components';

import { FatalAttraction } from '@vpr/tracking';

import { translate } from '@vimeo/i18n-runtime';
import { slate } from '@vimeo/iris/color';
import { buildUpgradePlanUrl } from '@vimeo/picox';

import appLinks from './AppsLinks';

interface LinkItem {
  label: string;
  href: string;
  fa?: { container: string; component: string; keyword: string; target?: string };
}

export const Link = styled.a`
  color: ${slate(200)};
  text-decoration: none;
  padding: ${rem(12)} 0;
  display: block;
  &:hover {
    color: ${slate(500)};
  }
`;

export function Upgrade() {
  return (
    <Link
      href={buildUpgradePlanUrl({
        paywallTrigger: 'footer_upgrade_link',
        paywallLocation: 'footer',
        paywallFeature: 'general',
      })}
      onClick={() => {
        FatalAttraction.trackClick({ container: 'footer', component: 'upgrade' });
      }}
    >
      {translate('Upgrade')}
    </Link>
  );
}

export function Help() {
  return (
    <Link
      href="/help"
      onClick={() => {
        FatalAttraction.trackClick({ container: 'footer', component: 'help' });
      }}
    >
      {translate('Help')}
    </Link>
  );
}

export interface LegalLink {
  label: string;
  'aria-label': string;
  href: string;
  title: string;
  forSimplifiedSite: boolean;
}

export const getLegalLinks = (): LegalLink[] => [
  {
    label: translate('Terms'),
    'aria-label': translate('Terms'),
    href: '/terms',
    title: translate('Terms & Conditions'),
    forSimplifiedSite: true,
  },
  {
    label: translate('Privacy'),
    'aria-label': translate('Privacy'),
    href: '/privacy',
    title: translate('Privacy Policy'),
    forSimplifiedSite: true,
  },
  {
    label: translate('U.S. State Privacy'),
    'aria-label': translate('U.S. State Privacy'),
    href: '/privacy/us-state-privacy',
    title: translate('U.S. State Privacy'),
    forSimplifiedSite: false,
  },
  {
    label: 'Impressum',
    'aria-label': 'Impressum',
    href: '/leo/guidelines/impressum',
    title: 'Impressum',
    forSimplifiedSite: false,
  },
  {
    label: translate('Copyright'),
    'aria-label': translate('Copyright'),
    href: '/dmca',
    title: translate('Copyright Information'),
    forSimplifiedSite: false,
  },
  {
    label: translate('Cookies'),
    'aria-label': translate('Cookies'),
    href: '/cookie_policy',
    title: translate('Learn how Vimeo uses cookies'),
    forSimplifiedSite: false,
  },
];

const getVimeoLinks = (terminateContractQualifies: boolean) => {
  const terminateContract = {
    label: translate('Terminate contracts here'),
    href: '/settings/billing/membership_plan/optout',
    fa: {
      container: 'footer',
      component: 'logged_in',
      keyword: 'terminate_here',
      target: '/settings/billing/membership_plan/optout',
    },
  };

  const otherLinks = [
    { label: translate('Pricing'), href: '/upgrade-plan' },
    { label: translate('Upload'), href: '/upload' },
    { label: translate('Staff Picks'), href: '/channels/staffpicks' },
    { label: translate('On Demand'), href: '/ondemand' },
    { label: translate('Vimeo OTT'), href: '/ott' },
    { label: translate('Site map'), href: '/site_map' },
    { label: translate('About'), href: '/about' },
    {
      label: translate('Investor Relations'),
      href: 'https://investors.vimeo.com',
      fa: { container: 'footer', component: 'vimeo', keyword: 'investor_relations' },
    },
    {
      label: translate('Press'),
      href: 'https://press.vimeo.com/',
      fa: { container: 'footer', component: 'vimeo', keyword: 'press' },
    },
    { label: translate('Jobs'), href: '/careers' },
  ];

  return terminateContractQualifies ? [terminateContract, ...otherLinks] : otherLinks;
};

// This is a hack to get around how translations are being bundled right now
// and to avoid having to duplicate these links in multiple places
export const getSiteMapLinks = (
  terminateContractQualifies: boolean,
): Array<{
  header: string;
  links: Array<LinkItem | null>;
}> => {
  const siteMapLinkItems = [
    {
      header: translate('Product'),
      links: [
        {
          label: translate('Interactive Video'),
          href: '/features/interactive-video',
          fa: {
            container: 'footer',
            component: 'features',
            keyword: 'interactive_video',
            target: '/features/interactive-video',
          },
        },
        {
          label: translate('Online Video Editor'),
          href: '/video-editor',
          fa: {
            container: 'footer',
            component: 'video_editor',
            keyword: 'video_editor',
            target: '/video-editor',
          },
        },
        {
          label: translate('Auto Captioning'),
          href: '/features/auto-caption',
          fa: {
            container: 'footer',
            component: 'features',
            keyword: 'auto_cc',
            target: '/features/auto-caption',
          },
        },
        {
          label: translate('Webinar Software'),
          href: '/features/webinar',
          fa: {
            container: 'footer',
            component: 'features',
            keyword: 'webinar',
            target: '/features/webinar',
          },
        },
        {
          label: translate('Virtual Events'),
          href: '/features/virtual-events',
          fa: {
            container: 'footer',
            component: 'features',
            keyword: 'live_events',
            target: '/features/virtual-events',
          },
        },
        { label: translate('Video Player'), href: '/features/video-player' },
        {
          label: translate('Video Library'),
          href: '/features/video-library',
          fa: { container: 'footer', component: 'features', keyword: 'video_library' },
        },
        {
          label: translate('Create Video Maker'),
          href: '/create',
          fa: { container: 'footer', component: 'solutions', keyword: 'create' },
        },
        { label: translate('Live streaming'), href: '/features/livestreaming' },
        { label: translate('Screen Recorder'), href: '/features/screen-recorder' },
        { label: translate('Video Privacy'), href: '/features/video-privacy' },
        { label: translate('Video Collaboration'), href: '/features/video-collaboration' },
        { label: translate('Video Marketing'), href: '/features/video-marketing' },
        { label: translate('OTT Video Monetization'), href: '/ott?mkc=entprsb' },
        { label: translate('Video Analytics'), href: '/features/video-analytics' },
        { label: translate('Video Hosting'), href: '/features/online-video-hosting' },
        {
          label: translate('Hire Video Pros'),
          href: '/for-hire',
          fa: { container: 'footer', component: 'solutions', keyword: 'for_hire' },
        },
      ],
    },
    {
      header: translate('Resources'),
      links: [
        { label: translate('Help Center'), href: '/help' },
        { label: translate('Blog'), href: '/blog' },
        {
          label: translate('Our Customers'),
          href: '/customers',
          fa: {
            container: 'footer',
            component: 'resources',
            keyword: 'customer_logos',
            target: '/customers',
          },
        },
        { label: translate('Video School'), href: '/blog/video-school' },
        { label: translate('OTT Resources'), href: 'https://ott.vimeo.com/resources/' },
        { label: translate('Developers'), href: 'https://developer.vimeo.com/' },
        { label: translate('Become a Partner'), href: '/partners' },
        { label: translate('Join Vimeo Experts'), href: '/experts' },
        { label: translate('Guidelines'), href: '/help/guidelines' },
      ],
    },
    {
      header: translate('Apps'),
      links: appLinks(),
    },
    {
      header: 'Vimeo',
      links: getVimeoLinks(terminateContractQualifies),
    },
  ];

  return siteMapLinkItems;
};

export const getAbsoluteUrl = (href: string) => {
  return !href.startsWith('https://') ? 'https://vimeo.com' + href : href;
};
